import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useIsAdmin } from "@/hooks/useAdmin";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Navigate } from "react-router-dom";
import { Search, Download, Users, Mail } from "lucide-react";

interface EarlyAccessSignup {
  id: string;
  email: string;
  user_type: string;
  gdpr_consent_at: string;
  created_at: string;
}

function useEarlyAccessSignups() {
  return useQuery({
    queryKey: ['early-access-signups'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('early_access_signups')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as EarlyAccessSignup[];
    },
  });
}

function getUserTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    'brand': 'Brand',
    'remaker': 'Remaker',
    'sorting-facility': 'Sorting Facility',
  };
  return labels[type] || type;
}

function getUserTypeBadgeVariant(type: string): "default" | "secondary" | "outline" {
  switch (type) {
    case 'brand':
      return 'default';
    case 'remaker':
      return 'secondary';
    case 'sorting-facility':
      return 'outline';
    default:
      return 'outline';
  }
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('sv-SE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AdminEarlyAccess() {
  const { user, loading: authLoading } = useAuth();
  const { data: isAdmin, isLoading: adminLoading } = useIsAdmin();
  const { data: signups, isLoading: signupsLoading } = useEarlyAccessSignups();
  const [searchQuery, setSearchQuery] = useState("");

  if (authLoading || adminLoading) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!isAdmin) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-12 text-center">
          <h1 className="mb-4">Access Denied</h1>
          <p className="text-muted-foreground">You don't have admin access.</p>
        </div>
      </Layout>
    );
  }

  const filteredSignups = signups?.filter((signup) => {
    const query = searchQuery.toLowerCase();
    return (
      signup.email.toLowerCase().includes(query) ||
      signup.user_type.toLowerCase().includes(query) ||
      getUserTypeLabel(signup.user_type).toLowerCase().includes(query)
    );
  });

  const exportToCSV = () => {
    if (!filteredSignups || filteredSignups.length === 0) return;

    const headers = ['Email', 'User Type', 'GDPR Consent Date', 'Signup Date'];
    const rows = filteredSignups.map((signup) => [
      signup.email,
      getUserTypeLabel(signup.user_type),
      formatDate(signup.gdpr_consent_at),
      formatDate(signup.created_at),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `early-access-signups-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const stats = {
    total: signups?.length || 0,
    brands: signups?.filter((s) => s.user_type === 'brand').length || 0,
    remakers: signups?.filter((s) => s.user_type === 'remaker').length || 0,
    sortingFacilities: signups?.filter((s) => s.user_type === 'sorting-facility').length || 0,
  };

  return (
    <Layout>
      <div className="container mx-auto px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="mb-2">Early Access Signups</h1>
            <p className="text-muted-foreground">View and export early access registrations</p>
          </div>

          <Button onClick={exportToCSV} disabled={!filteredSignups?.length}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Mail className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.brands}</p>
                  <p className="text-sm text-muted-foreground">Brands</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-secondary/20 rounded-lg">
                  <Mail className="h-5 w-5 text-secondary-foreground" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.remakers}</p>
                  <p className="text-sm text-muted-foreground">Remakers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-muted rounded-lg">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.sortingFacilities}</p>
                  <p className="text-sm text-muted-foreground">Sorting</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Registrations ({filteredSignups?.length || 0})
              </CardTitle>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search email or type..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {signupsLoading ? (
              <p className="text-center text-muted-foreground py-8">Loading signups...</p>
            ) : filteredSignups?.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                {searchQuery ? 'No signups match your search.' : 'No signups yet.'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>GDPR Consent</TableHead>
                      <TableHead>Signed Up</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSignups?.map((signup) => (
                      <TableRow key={signup.id}>
                        <TableCell className="font-medium">{signup.email}</TableCell>
                        <TableCell>
                          <Badge variant={getUserTypeBadgeVariant(signup.user_type)}>
                            {getUserTypeLabel(signup.user_type)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(signup.gdpr_consent_at)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(signup.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
